import React, {useCallback} from 'react';
// Direct imports per bundle-barrel-imports rule (rule 2.1)
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {applySuggestion, revertSuggestion} from '@handlewithcare/prosemirror-suggest-changes';
import {getMarkerTheme} from './markerTheme';
import CommentsSection from '../../../comments/CommentsSection';
import {useYCommentsCount} from '../../../comments/useYCommentsCount';

/**
 * Fallback rejection handler for block-level insertion node marks.
 *
 * The library's revertSuggestion only searches inline (text) marks when
 * looking for the suggestionId. A whole-block insertion (e.g. an inserted
 * table) is tracked with a node mark on the block itself, whose children
 * never carry inline marks. When revertSuggestion finds nothing it returns
 * false without dispatching. This function handles that gap by walking the
 * document for any non-inline node whose node-marks include an insertion
 * mark with the given id, collecting their ranges, then deleting them in
 * reverse order so earlier deletions don't invalidate later positions.
 *
 * Returns true when at least one node was deleted, false otherwise.
 */
function rejectInsertedBlocks(view, suggestionId) {
    const {state} = view;
    const {insertion} = state.schema.marks;
    if (!insertion) return false;

    const rangesToDelete = [];
    state.doc.descendants((node, pos) => {
        if (node.isInline) return true;
        const mark = node.marks.find(
            (m) => m.type === insertion && m.attrs.id === suggestionId
        );
        if (!mark) return true;
        rangesToDelete.push({from: pos, to: pos + node.nodeSize});
        // Don't descend — children are covered by deleting the parent range.
        return false;
    });

    if (!rangesToDelete.length) return false;

    // Delete in reverse order so positions remain valid after each step.
    const tr = state.tr;
    [...rangesToDelete].reverse().forEach(({from, to}) => {
        tr.deleteRange(from, to);
    });
    tr.setMeta('addToHistory', true);
    view.dispatch(tr);
    return true;
}

// Static sx objects hoisted outside component to avoid re-creation on every render (rule 6.3)
const HEADER_STACK_SX = {width: '100%', minHeight: 40, p: 1, cursor: 'pointer'};
const ICON_BOX_BASE_SX = {
    width: 26, height: 26, borderRadius: 999, display: 'flex',
    alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
};
const TEXT_BOX_SX = {flex: 1, overflow: 'hidden'};
const TITLE_SX = {fontWeight: 600, lineHeight: 1.2};
const CAPTION_SX = {color: 'text.secondary', display: 'block', lineHeight: 1.2, fontSize: '0.7rem'};
const ACCEPT_BTN_SX = {
    flex: 1, fontSize: '0.7rem', py: 0.25,
    bgcolor: '#1e8e3e', '&:hover': {bgcolor: '#1a7a37'},
};
const REJECT_BTN_SX = {flex: 1, fontSize: '0.7rem', py: 0.25, borderColor: '#d93025', color: '#d93025'};
const AI_CHIP_SX = {height: 16, fontSize: '0.6rem', bgcolor: 'rgba(26,115,232,0.1)', color: '#1a73e8', '& .MuiChip-icon': {fontSize: '0.7rem', color: '#1a73e8'}};
const COMMENT_SECTION_SX = {width: '100%', overflow: 'hidden'};

const ELEMENT_NAME = 'suggestion';

/**
 * Margin-rail pill for a single suggestion (insertion, deletion, or change).
 * Shows change type, author (with AI badge if AI-authored), accept/reject actions,
 * and a comment thread for discussing the suggestion — all in the margin rail.
 *
 * @param {Object} props
 * @param {Object} props.marker        - Marker descriptor from marginMarkersPlugin
 * @param {boolean} props.isActive     - Whether this pill is focused/expanded
 * @param {Function} props.onClick     - Called when the pill header is clicked
 * @param {import('prosemirror-view').EditorView} props.view - Editor view (for accept/reject)
 */
export default function SuggestionMarker({marker, isActive, onClick, view}) {
    const cfg = getMarkerTheme(marker?.type);
    const Icon = cfg.Icon;

    const isInsert = marker?.type === 'insertion';
    const isChange = marker?.type === 'change';
    const label = isChange ? 'Change' : isInsert ? 'Addition' : 'Deletion';

    // Author info encoded in the suggestion id by createEditorView's generateId
    const userId = marker?.markAttrs?.userId ?? null;
    const isAI = marker?.markAttrs?.isAI ?? false;

    // Resolve a human-readable author label.
    // If the suggestion was made by the current user we show "You".
    // For AI we show a chip instead. For others we show a truncated uid.
    const currentUser = view?.__currentUser;
    const authorLabel = (() => {
        if (!userId) return null;
        if (isAI) return null; // shown as chip below
        if (currentUser?.uid && currentUser.uid === userId) return 'You';
        // Use displayName if we happen to have it (e.g. from Yjs awareness or user prop)
        if (currentUser?.displayName && currentUser.uid === userId) return currentUser.displayName;
        return userId.length > 12 ? `${userId.slice(0, 8)}…` : userId;
    })();

    // The numeric suggestion id used by the library's accept/reject commands
    const suggestionId = marker?.markAttrs?.id;

    // Comment count badge — reuses the same Yjs comment system as regular comments
    const {count: commentCount, isLoaded: commentsLoaded} = useYCommentsCount({
        elementName: ELEMENT_NAME,
        elementId: String(suggestionId ?? ''),
    });
    const hasComments = commentsLoaded && commentCount > 0;

    // useCallback so handler references are stable across renders (rule 8.3)
    const handleAccept = useCallback((e) => {
        e.stopPropagation();
        if (!view || suggestionId == null) return;
        applySuggestion(suggestionId)(view.state, view.dispatch, view);
        view.focus();
    }, [view, suggestionId]);

    const handleReject = useCallback((e) => {
        e.stopPropagation();
        if (!view || suggestionId == null) return;
        // revertSuggestion handles inline (text-node) marks but may miss whole-block
        // insertion node marks (e.g. a table inserted as a unit). Run both: let the
        // library handle inline content, then clean up any remaining block nodes that
        // still carry the matching insertion node mark.
        revertSuggestion(suggestionId)(view.state, view.dispatch, view);
        rejectInsertedBlocks(view, suggestionId);
        view.focus();
    }, [view, suggestionId]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(e); }
    }, [onClick]);

    // sx objects that depend on runtime values (must stay inline or be derived)
    const containerSx = {
        width: '100%',
        minHeight: 40,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: isActive ? `1px solid ${cfg.border}` : '1px solid transparent',
        boxShadow: isActive ? '0 6px 18px rgba(0,0,0,0.18)' : 'none',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        userSelect: 'none',
        zIndex: isActive ? 999 : 'auto',
        ...(isActive ? {} : {'&:hover': {bgcolor: 'rgba(0,0,0,0.04)'}}),
    };

    const iconBoxSx = {...ICON_BOX_BASE_SX, bgcolor: cfg.bg, border: `1px solid ${cfg.border}`};

    return (
        <Box className="pm-margin-marker" sx={containerSx}>
            {/* Header row */}
            <Stack
                role="button"
                direction="row"
                alignItems="center"
                gap={1}
                tabIndex={0}
                onClick={onClick}
                onKeyDown={handleKeyDown}
                sx={HEADER_STACK_SX}
            >
                {/* Use explicit ternary per rule 6.9 — avoids rendering `false` */}
                {Icon ? (
                    <Box sx={iconBoxSx}>
                        <Icon fontSize="small" sx={{color: cfg.color}}/>
                    </Box>
                ) : null}
                <Box sx={TEXT_BOX_SX}>
                    <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
                        <Typography variant="subtitle2" sx={TITLE_SX}>
                            {label}
                        </Typography>
                        {/* AI badge — shown when the suggestion was made by the AI assistant */}
                        {isAI ? (
                            <Tooltip title="Suggested by AI Assistant" arrow>
                                <Chip
                                    size="small"
                                    icon={<SmartToyIcon/>}
                                    label="AI"
                                    sx={AI_CHIP_SX}
                                />
                            </Tooltip>
                        ) : null}
                        {/* Comment count badge */}
                        {hasComments ? (
                            <Chip
                                size="small"
                                label={`${commentCount} comment${commentCount === 1 ? '' : 's'}`}
                                sx={{height: 16, fontSize: '0.6rem'}}
                            />
                        ) : null}
                    </Stack>
                    {/* Show a short author label for human-authored suggestions */}
                    {authorLabel ? (
                        <Typography variant="caption" sx={CAPTION_SX} noWrap>
                            by {authorLabel}
                        </Typography>
                    ) : null}
                </Box>
            </Stack>

            {/* Expanded content — only shown when active (explicit ternary per rule 6.9) */}
            {isActive ? (
                <Box>
                    {/* Accept / Reject actions */}
                    <Stack direction="row" gap={1} px={1} pb={1}>
                        <Button
                            size="small"
                            variant="contained"
                            sx={ACCEPT_BTN_SX}
                            onClick={handleAccept}
                        >
                            Accept
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            sx={REJECT_BTN_SX}
                            onClick={handleReject}
                        >
                            Reject
                        </Button>
                    </Stack>

                    {/* Comment thread — same Yjs system as regular comment markers.
                        Thread key: "suggestion:<suggestionId>" */}
                    <Box
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        sx={COMMENT_SECTION_SX}
                    >
                        <CommentsSection
                            props={{
                                fullHeight: false,
                                elementName: ELEMENT_NAME,
                                elementId: String(suggestionId ?? ''),
                                autoFocus: false,
                            }}
                        />
                    </Box>
                </Box>
            ) : null}
        </Box>
    );
}
